import { Select } from '@components/index';
import { IconButton } from '@components/iconButton';
import { Box, Tooltip, Typography } from '@mui/material';
import { AvailableBrokerageOfferingsItem } from '@models/productOffering';
import { paperProps, boxStyles } from '@pages/NewQuote/scope/ScopePanel.styles';
import { useProductOfferings } from '@services/productOffering';
import { useNewQuoteStore } from '@store/NewQuote';
import { getEarliestEffectiveOn, getLatestEffectiveThrough } from '@utils/brokerages';
import { addDays } from 'date-fns';
import { uniqBy } from 'lodash';
import { InfoCircle } from '@assets/svgs';
import { useProductLabels } from '@services/productLabel';
import { useProductOptions } from '@services/productOption';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useEffect } from 'react';
import { shallow } from 'zustand/shallow';
import { useStore } from '@store/GlobalStore';
import { ProductOfferingId } from '@models/newQuote';
import { HOABusinessClassHelpText } from './HOABusinessClassHelpText';

type TInsuranceProductSelection = {
  handleProductOfferingChange?: (selectedValue: any) => void;
  handleProductOptionChange: (selectedValue: any) => void;
  handleProductLabelChange: (selectedValue: any) => void;
  quotePresetSelectedInsuranceProductId?: string;
  isRenewalMode?: boolean;
};

export const InsuranceProductSelection = ({
  handleProductOfferingChange,
  handleProductOptionChange,
  handleProductLabelChange,
  quotePresetSelectedInsuranceProductId,
  isRenewalMode = false,
}: TInsuranceProductSelection) => {
  const { isMobileScreen } = useGetScreenSize();
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const { scope, setFormLoading } = useNewQuoteStore();
  const { brokerageAgency, brokerageProducer, productOffering, productLabel, insuranceProductId, productOption } =
    scope;
  const { productOfferings, isLoading: isProductOfferingLoading } = useProductOfferings({
    brokerageId: brokerageAgency?.value,
  });
  const { productOptions, isLoading: isProductOptionsLoading } = useProductOptions(
    quotePresetSelectedInsuranceProductId ?? insuranceProductId,
  );
  const { productLabels, isLoading: isProductLabelsLoading } = useProductLabels(
    quotePresetSelectedInsuranceProductId ?? insuranceProductId,
  );

  const uniqueOfferings = productOfferings?.items.length
    ? uniqBy(productOfferings.items, (brokerageOffering) => brokerageOffering.id).map((p) => ({
        ...p,
        brokerageOfferingAvailability: {
          jurisdictions: p.brokerageOfferingAvailability.jurisdictions.map((j) => ({
            ...j,
            effectiveOn: new Date(j.effectiveOn),
            effectiveThrough: new Date(j.effectiveThrough),
          })),
        },
      }))
    : [];
  let effectiveProducts = uniqueOfferings.filter((item: AvailableBrokerageOfferingsItem) => {
    const effectiveOn = getEarliestEffectiveOn([...item.brokerageOfferingAvailability.jurisdictions]);
    const effectiveThrough = getLatestEffectiveThrough([...item.brokerageOfferingAvailability.jurisdictions]);
    if (effectiveThrough) {
      return effectiveOn <= addDays(new Date(), 120) && effectiveThrough > new Date(Date.now());
    }
    return effectiveOn <= addDays(new Date(), 120);
  });

  // Renewal restriction: only HOA / COA offerings (label or product name contains these tokens)
  if (isRenewalMode) {
    effectiveProducts = effectiveProducts.filter((item) => {
      return [ProductOfferingId.HomeOwnersAssociation, ProductOfferingId.CondoOwnersAssociation].includes(
        item.id as any,
      );
    });

    if (scope.brokerageAgency && !effectiveProducts.length) {
      console.error('No effective HOA/COA products available for renewal for selected brokerage');
    }
  }
  const showLabel = !quotePresetSelectedInsuranceProductId;

  const isLoading = isProductOfferingLoading || isProductOptionsLoading || isProductLabelsLoading;

  useEffect(() => {
    setFormLoading(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const isHOAorCOA = [ProductOfferingId.CondoOwnersAssociation, ProductOfferingId.HomeOwnersAssociation].includes(
    productOffering?.value ?? ('' as any),
  );

  // Internal users can see the Product Label dropdown only when:
  // - the selected product is not HOA/COA
  // - or the selected product is HOA/COA but in renewal mode
  const shouldShowProductLabel = isInternalUser && (!isHOAorCOA || (isHOAorCOA && isRenewalMode));
  const shouldShowBusinessClassHelpText = productOffering?.value === ProductOfferingId.HomeOwnersAssociation;

  return (
    <>
      {showLabel && (
        <Typography
          variant='h4'
          sx={{
            mt: '10px',
          }}
        >
          Which Insurance Product do you need?
        </Typography>
      )}
      {handleProductOfferingChange && (
        <Box sx={boxStyles}>
          <Select
            name='Product'
            sx={!isMobileScreen ? { width: '300px' } : undefined}
            inputLabel='Product'
            fullWidth={isMobileScreen}
            data-testid='Product'
            items={effectiveProducts
              .sort((a, b) => (a.label ?? a.insuranceProduct.name).localeCompare(b.label ?? b.insuranceProduct.name))
              .map((item: AvailableBrokerageOfferingsItem) => ({
                key: item.id,
                value: item.id,
                label: item.label ?? item.insuranceProduct.name,
              }))}
            MenuProps={{
              ...paperProps,
            }}
            value={productOffering?.value}
            onChangeSelect={(eventValue) => {
              const selectedValue = effectiveProducts?.find(
                (p: AvailableBrokerageOfferingsItem) => p.id === eventValue,
              );
              handleProductOfferingChange(selectedValue);
            }}
            disabled={!brokerageProducer || !effectiveProducts?.length}
          />
        </Box>
      )}
      {productOffering && productOptions.items.length > 0 && (
        <Box
          sx={{
            ...boxStyles,
            mt: showLabel ? '0px' : 2,
          }}
        >
          <Select
            sx={!isMobileScreen ? { width: '300px' } : undefined}
            inputLabel='Business Class'
            data-testid='Business Class'
            fullWidth={isMobileScreen}
            items={productOptions?.items.map((item) => ({
              key: item.id,
              value: item.id,
              label: item.name,
              ...item,
            }))}
            MenuProps={{
              ...paperProps,
            }}
            value={productOption?.id}
            onChangeSelect={(eventValue) => {
              const selectedValue = productOptions.items.find((p) => p.id === eventValue);
              handleProductOptionChange(selectedValue);
            }}
            externalIcon={
              productOption?.description ? (
                <Tooltip placement='top' title={productOption.description}>
                  <IconButton variant='circularDark'>
                    <InfoCircle />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          />
        </Box>
      )}
      {productOffering && productOptions.items.length > 0 && shouldShowBusinessClassHelpText && (
        <Box sx={boxStyles}>
          <HOABusinessClassHelpText />
        </Box>
      )}
      {productOffering && productLabels.length > 0 && shouldShowProductLabel && (
        <Box sx={boxStyles}>
          <Select
            sx={!isMobileScreen ? { width: '300px' } : undefined}
            inputLabel='Product Label'
            fullWidth={isMobileScreen}
            data-testid='Product Label'
            items={productLabels?.map((item) => ({
              key: item.id,
              value: item.id,
              label: item.name,
              ...item,
            }))}
            MenuProps={{
              ...paperProps,
            }}
            value={productLabel?.id}
            onChangeSelect={(eventValue) => {
              const selectedValue = productLabels.find((p) => p.id === eventValue);
              handleProductLabelChange(selectedValue);
            }}
          />
        </Box>
      )}
    </>
  );
};
